import { useState } from 'react';
import SEO from '@/components/common/SEO';
import {
  HelpCircle,
  BookOpen,
  MessageCircle,
  Mail,
  ChevronDown,
  ChevronRight,
  Home,
  MapPin,
  Calendar,
  Users,
  Briefcase,
  Globe,
  CreditCard,
  Shield,
  Search,
  X,
  Send,
  Lightbulb,
  Bug,
  Sparkles,
  CheckCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/common';

// FAQ Data organized by category
const FAQ_CATEGORIES = [
  {
    id: 'getting-started',
    name: 'Getting Started',
    nameKo: '시작하기',
    icon: BookOpen,
    faqs: [
      {
        question: 'What is KoreanHub?',
        questionKo: 'KoreanHub이 무엇인가요?',
        answer:
          'KoreanHub is a comprehensive platform for the Korean diaspora community worldwide. We help Koreans living abroad find housing, discover local Korean businesses, connect with community events, and access immigration resources.',
      },
      {
        question: 'Is KoreanHub free to use?',
        questionKo: 'KoreanHub은 무료인가요?',
        answer:
          'Yes! Browsing listings, reading community posts, and accessing our guides is completely free. Some premium features like posting job listings or attorney services require a subscription.',
      },
      {
        question: 'How do I create an account?',
        questionKo: '계정은 어떻게 만드나요?',
        answer:
          'Click the "Sign Up" button in the top navigation, enter your email and create a password. You can also sign up using your Google account for faster registration.',
      },
      {
        question: 'What countries does KoreanHub cover?',
        questionKo: '어떤 나라를 다루나요?',
        answer:
          'We currently focus on 8 major destinations for Korean expats: United States, Canada, Mexico, United Kingdom, Australia, Indonesia, China, and Singapore. We are continuously expanding to more countries.',
      },
    ],
  },
  {
    id: 'events',
    name: 'Events',
    nameKo: '이벤트',
    icon: Calendar,
    faqs: [
      {
        question: 'How do I find Korean events near me?',
        questionKo: '근처 한인 이벤트를 어떻게 찾나요?',
        answer:
          'Go to the Events section and use the location filter to find events in your city. You can also filter by category (cultural, business, social, etc.) and date.',
      },
      {
        question: 'Can I post my own event?',
        questionKo: '이벤트를 등록할 수 있나요?',
        answer:
          'Yes! Registered users can submit events for review. Go to Events and click "Submit Event." Include details like date, location, and description. Events are reviewed within 24 hours.',
      },
      {
        question: 'Are events free?',
        questionKo: '이벤트가 무료인가요?',
        answer:
          'Event pricing is set by organizers. Many community events are free, while some workshops or special events may have a fee. Pricing is always shown on the event listing.',
      },
    ],
  },
  {
    id: 'community',
    name: 'Community',
    nameKo: '커뮤니티',
    icon: Users,
    faqs: [
      {
        question: 'What can I post in the community?',
        questionKo: '커뮤니티에 무엇을 올릴 수 있나요?',
        answer:
          'Share questions, tips, recommendations, or discussions related to Korean expat life. Categories include housing advice, job hunting, local recommendations, cultural experiences, and more.',
      },
      {
        question: 'Are there community guidelines?',
        questionKo: '커뮤니티 규칙이 있나요?',
        answer:
          'Yes. Be respectful, no spam or self-promotion, no hate speech, and keep discussions relevant. Violations may result in post removal or account suspension.',
      },
      {
        question: 'Can I post in Korean?',
        questionKo: '한국어로 글을 올릴 수 있나요?',
        answer:
          'Absolutely! Our community is bilingual. You can post in Korean, English, or both. Many members appreciate bilingual posts for accessibility.',
      },
    ],
  },
  {
    id: 'immigration',
    name: 'Immigration & Visas',
    nameKo: '이민 & 비자',
    icon: Briefcase,
    faqs: [
      {
        question: 'Is the visa information on KoreanHub official?',
        questionKo: '비자 정보가 공식적인가요?',
        answer:
          'Our visa guides are researched and compiled for general information only. Always verify requirements with official government sources or consult an immigration attorney for your specific situation.',
      },
      {
        question: 'How do visa-sponsoring job listings work?',
        questionKo: '비자 스폰서 구인 공고는 어떻게 하나요?',
        answer:
          'Employers can post job listings that offer visa sponsorship. These are paid listings ($99/month for basic, $199/month for featured). Jobs are reviewed before going live to ensure legitimacy.',
      },
      {
        question: 'How do I find an immigration attorney?',
        questionKo: '이민 변호사를 어떻게 찾나요?',
        answer:
          'Visit our Immigration page and click the "Attorneys" tab. You can filter by country and specialty. All listed attorneys have been verified as licensed professionals.',
      },
    ],
  },
  {
    id: 'country-guides',
    name: 'Country Guides',
    nameKo: '국가 가이드',
    icon: Globe,
    faqs: [
      {
        question: 'What do the country guides include?',
        questionKo: '국가 가이드에는 무엇이 포함되나요?',
        answer:
          'Each guide covers cost of living, major Korean communities, visa information, practical tips (currency, timezone, emergency numbers), cultural adaptation advice, and links to local resources.',
      },
      {
        question: 'How often are guides updated?',
        questionKo: '가이드가 얼마나 자주 업데이트되나요?',
        answer:
          'We review and update our guides quarterly to ensure information remains accurate. Major changes (visa policy updates, etc.) are updated immediately.',
      },
    ],
  },
  {
    id: 'payments',
    name: 'Payments & Billing',
    nameKo: '결제',
    icon: CreditCard,
    faqs: [
      {
        question: 'What payment methods are accepted?',
        questionKo: '어떤 결제 수단을 사용할 수 있나요?',
        answer:
          'We accept all major credit cards (Visa, Mastercard, American Express) and debit cards through Stripe. Some regions may have additional local payment options.',
      },
      {
        question: 'Is my payment information secure?',
        questionKo: '결제 정보가 안전한가요?',
        answer:
          'Yes. We use Stripe for payment processing, which is PCI-DSS compliant. We never store your full card details on our servers.',
      },
      {
        question: 'How do refunds work?',
        questionKo: '환불은 어떻게 하나요?',
        answer:
          'Refund eligibility depends on the specific service or listing\'s cancellation policy. Approved refunds are processed within 5-10 business days to your original payment method.',
      },
    ],
  },
  {
    id: 'account',
    name: 'Account & Privacy',
    nameKo: '계정 & 개인정보',
    icon: Shield,
    faqs: [
      {
        question: 'How do I reset my password?',
        questionKo: '비밀번호를 어떻게 재설정하나요?',
        answer:
          'Click "Login" then "Forgot Password." Enter your email address and we\'ll send you a reset link. The link expires after 24 hours.',
      },
      {
        question: 'How do I delete my account?',
        questionKo: '계정을 어떻게 삭제하나요?',
        answer:
          'Go to Profile > Settings > Delete Account. This action is permanent and will remove all your data, bookings, and posts. Active bookings must be completed or cancelled first.',
      },
      {
        question: 'How is my data protected?',
        questionKo: '제 데이터는 어떻게 보호되나요?',
        answer:
          'We use industry-standard encryption and security practices. Your personal data is never sold to third parties. See our Privacy Policy for full details.',
      },
    ],
  },
];

// Quick Start Guides
const QUICK_START_GUIDES = [
  {
    title: 'Send Money to Korea',
    titleKo: '한국으로 송금하기',
    description: 'Compare fees, exchange-rate margins and tax reporting',
    icon: Home,
    link: '/guides/sending-money-to-korea',
    steps: ['Compare the real cost', 'Check the reporting thresholds', 'Pick the right provider'],
  },
  {
    title: 'Get an F-4 Visa',
    titleKo: 'F-4 비자 받기',
    description: 'Eligibility, documents and the order to do them in',
    icon: MapPin,
    link: '/guides/f4-visa-guide',
    steps: ['Confirm your eligibility route', 'Gather Korean family records', 'File at your consulate'],
  },
  {
    title: 'Expat Life Hacks',
    titleKo: '해외생활 꿀팁',
    description: 'Settlement guides, local apps, housing and tax',
    icon: Users,
    link: '/guides?category=life',
    steps: ['Browse by pillar', 'Read the settlement guides', 'Send us what the guide missed'],
  },
];

// Feedback types
const FEEDBACK_TYPES = [
  { id: 'bug', label: 'Bug Report', labelKo: '버그 신고', icon: Bug, color: 'red' },
  { id: 'feature', label: 'Feature Request', labelKo: '기능 요청', icon: Sparkles, color: 'purple' },
  { id: 'improvement', label: 'Improvement', labelKo: '개선 제안', icon: Lightbulb, color: 'amber' },
  { id: 'other', label: 'Other', labelKo: '기타', icon: MessageCircle, color: 'gray' },
];

// FAQ Item Component
function FAQItem({
  faq,
  isOpen,
  onToggle,
}: {
  faq: { question: string; questionKo: string; answer: string };
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-gray-200 dark:border-slate-700 last:border-0">
      <SEO title="Help Centre" description="Answers on remittance, visas, settlement and using KoreanHub." path="/help" />
      <button
        onClick={onToggle}
        className="w-full py-4 flex items-start justify-between text-left hover:bg-gray-50 dark:hover:bg-slate-700/50 px-4 -mx-4 transition-colors"
      >
        <div>
          <h4 className="font-medium text-gray-900 dark:text-white">{faq.question}</h4>
          <p className="text-sm text-gray-500 dark:text-gray-400">{faq.questionKo}</p>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 flex-shrink-0 ml-4 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="pb-4 px-4 -mx-4">
          <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{faq.answer}</p>
        </div>
      )}
    </div>
  );
}

// Contact Support Modal
function ContactSupportModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  const handleClose = () => {
    setFormData({ name: '', email: '', subject: '', message: '' });
    setIsSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Contact Support</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">고객 지원 문의</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isSubmitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Message Sent!
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                We'll get back to you within 24-48 hours.
                <br />
                <span className="text-sm">24-48시간 내에 답변드리겠습니다.</span>
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name <span className="text-gray-400">(이름)</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email <span className="text-gray-400">(이메일)</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Subject <span className="text-gray-400">(제목)</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="What do you need help with?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Message <span className="text-gray-400">(내용)</span>
                </label>
                <textarea
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Describe your issue in detail..."
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Message
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// Feedback Modal
function FeedbackModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [formData, setFormData] = useState({
    type: 'improvement',
    email: '',
    title: '',
    description: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  const handleClose = () => {
    setFormData({ type: 'improvement', email: '', title: '', description: '' });
    setIsSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Submit Feedback</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">피드백 제출</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isSubmitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Thank You!
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                Your feedback helps us improve KoreanHub.
                <br />
                <span className="text-sm">소중한 피드백 감사합니다.</span>
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Feedback Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Feedback Type <span className="text-gray-400">(유형)</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {FEEDBACK_TYPES.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, type: type.id })}
                      className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
                        formData.type === type.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                      }`}
                    >
                      <type.icon className={`w-4 h-4 ${
                        formData.type === type.id
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-400'
                      }`} />
                      <div className="text-left">
                        <div className="text-sm font-medium">{type.label}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{type.labelKo}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email <span className="text-gray-400">(이메일 - optional)</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="For follow-up (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title <span className="text-gray-400">(제목)</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Brief summary of your feedback"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description <span className="text-gray-400">(설명)</span>
                </label>
                <textarea
                  required
                  rows={5}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder={
                    formData.type === 'bug'
                      ? 'What happened? What did you expect? Steps to reproduce...'
                      : 'Describe your idea or suggestion in detail...'
                  }
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit Feedback
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Help() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [openFAQs, setOpenFAQs] = useState<Set<string>>(new Set());
  const [showContactModal, setShowContactModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Filter FAQs based on search
  const filteredCategories = FAQ_CATEGORIES.map((category) => ({
    ...category,
    faqs: category.faqs.filter(
      (faq) =>
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.questionKo.includes(searchQuery) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter((category) => category.faqs.length > 0);

  const toggleFAQ = (categoryId: string, faqIndex: number) => {
    const key = `${categoryId}-${faqIndex}`;
    setOpenFAQs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <SEO title="Help Centre" description="Answers on remittance, visas, settlement and using KoreanHub." path="/help" />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <HelpCircle className="w-12 h-12" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-3">Help Center</h1>
            <p className="text-blue-100 text-lg mb-2">도움말 센터</p>
            <p className="text-blue-100 max-w-2xl mx-auto">
              Find answers to common questions, learn how to use KoreanHub, and get support when
              you need it.
            </p>

            {/* Search Bar */}
            <div className="mt-8 max-w-xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search for help... (검색)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/50"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Start Guides */}
        {!searchQuery && (
          <section className="mb-12">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              Quick Start Guides
              <span className="text-gray-500 dark:text-gray-400 font-normal text-base ml-2">
                빠른 시작 가이드
              </span>
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {QUICK_START_GUIDES.map((guide) => (
                <Link
                  key={guide.title}
                  to={guide.link}
                  className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-lg transition-all group"
                >
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-4">
                    <guide.icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                    {guide.title}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{guide.titleKo}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                    {guide.description}
                  </p>
                  <ol className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                    {guide.steps.map((step, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="w-4 h-4 bg-gray-100 dark:bg-slate-700 rounded-full flex items-center justify-center text-[10px] font-medium">
                          {i + 1}
                        </span>
                        {step}
                      </li>
                    ))}
                  </ol>
                  <div className="mt-4 flex items-center text-blue-600 dark:text-blue-400 text-sm font-medium group-hover:gap-2 transition-all">
                    <span>Get Started</span>
                    <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* FAQ Section */}
        <section>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            Frequently Asked Questions
            <span className="text-gray-500 dark:text-gray-400 font-normal text-base ml-2">
              자주 묻는 질문
            </span>
          </h2>

          {searchQuery ? (
            // Search Results
            <div className="space-y-6">
              {filteredCategories.length === 0 ? (
                <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
                  <Search className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">
                    No results found for "{searchQuery}"
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                    Try different keywords or browse categories below
                  </p>
                </div>
              ) : (
                filteredCategories.map((category) => (
                  <div
                    key={category.id}
                    className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden"
                  >
                    <div className="px-5 py-4 bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-700">
                      <div className="flex items-center gap-3">
                        <category.icon className="w-5 h-5 text-blue-500" />
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {category.name}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {category.nameKo}
                          </p>
                        </div>
                        <Badge variant="blue" size="sm" className="ml-auto">
                          {category.faqs.length} results
                        </Badge>
                      </div>
                    </div>
                    <div className="px-5">
                      {category.faqs.map((faq, index) => (
                        <FAQItem
                          key={index}
                          faq={faq}
                          isOpen={openFAQs.has(`${category.id}-${index}`)}
                          onToggle={() => toggleFAQ(category.id, index)}
                        />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            // Category Grid
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {FAQ_CATEGORIES.map((category) => (
                <div
                  key={category.id}
                  className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden"
                >
                  <button
                    onClick={() =>
                      setActiveCategory(activeCategory === category.id ? null : category.id)
                    }
                    className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                        <category.icon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {category.name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {category.nameKo}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="gray" size="sm">
                        {category.faqs.length}
                      </Badge>
                      <ChevronDown
                        className={`w-5 h-5 text-gray-400 transition-transform ${
                          activeCategory === category.id ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </button>

                  {activeCategory === category.id && (
                    <div className="px-5 border-t border-gray-200 dark:border-slate-700">
                      {category.faqs.map((faq, index) => (
                        <FAQItem
                          key={index}
                          faq={faq}
                          isOpen={openFAQs.has(`${category.id}-${index}`)}
                          onToggle={() => toggleFAQ(category.id, index)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Contact Support Section */}
        <section className="mt-12">
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 dark:from-slate-700 dark:to-slate-800 rounded-2xl p-8 text-white">
            <div className="max-w-3xl mx-auto text-center">
              <MessageCircle className="w-12 h-12 mx-auto mb-4 text-blue-400" />
              <h2 className="text-2xl font-bold mb-2">Still need help?</h2>
              <p className="text-slate-300 mb-6">
                Can't find what you're looking for? Our support team is here to help.
                <br />
                <span className="text-slate-400">
                  찾으시는 내용이 없으신가요? 저희 지원팀이 도와드리겠습니다.
                </span>
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => setShowContactModal(true)}
                  className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  Contact Support
                </button>
                <button
                  onClick={() => setShowFeedbackModal(true)}
                  className="inline-flex items-center justify-center gap-2 bg-slate-600 hover:bg-slate-500 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  <Lightbulb className="w-5 h-5" />
                  Submit Feedback
                </button>
              </div>
              <p className="text-sm text-slate-400 mt-4">
                Response time: Within 24-48 hours (business days)
              </p>
            </div>
          </div>
        </section>

        {/* Popular Resources */}
        <section className="mt-12">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
            Popular Resources
            <span className="text-gray-500 dark:text-gray-400 font-normal text-base ml-2">
              인기 리소스
            </span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/immigration"
              className="flex items-center gap-3 bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
            >
              <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Visa Guide</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">비자 가이드</p>
              </div>
            </Link>
            <Link
              to="/countries"
              className="flex items-center gap-3 bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
            >
              <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center">
                <Globe className="w-5 h-5 text-teal-600 dark:text-teal-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Country Guides</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">국가 가이드</p>
              </div>
            </Link>
            <Link
              to="/guides?category=life"
              className="flex items-center gap-3 bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
            >
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Community</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">커뮤니티</p>
              </div>
            </Link>
            <Link
              to="/events"
              className="flex items-center gap-3 bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
            >
              <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Events</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">이벤트</p>
              </div>
            </Link>
          </div>
        </section>
      </div>

      {/* Modals */}
      <ContactSupportModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} />
      <FeedbackModal isOpen={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} />
    </div>
  );
}
